import type { OpenImRawConversation } from '../appDto/types';

import { mapOpenImConversationToApiChat } from '../appDto/chat';
import { callOpenIm } from '../worker/connector';

let operationCounter = 0;

function nextOperationId(): string {
  operationCounter += 1;
  return `web-conv-op-${Date.now()}-${operationCounter}`;
}

export async function fetchOpenImConversations(selfUserId: string) {
  const raw = await callOpenIm<string>('getAllConversationList', nextOperationId());
  const parsed = (typeof raw === 'string'
    ? JSON.parse(raw || '[]')
    : raw) as OpenImRawConversation[] | { conversations?: OpenImRawConversation[] };
  // WASM historically returns a bare JSON array; some HTTP-shaped payloads wrap it.
  const conversations = Array.isArray(parsed)
    ? parsed
    : (parsed?.conversations ?? []);
  return conversations.map((conversation) => mapOpenImConversationToApiChat(conversation, selfUserId));
}

// `open_im_sdk.MarkConversationMessageAsRead(callback, operationID string, conversationID string)`
// (openim-sdk-core `open_im_sdk/conversation_msg.go:165`) -- verified against the Go source.
// `conversationId` here is the FULL composite id (e.g. `si_u1_u2`/`sg_g1`), never a bare peer/group
// id -- unlike most other OpenIM calls in this app, this one takes the SAME string this app already
// uses as `ApiChat.id`, since the SDK's own `conversationID` field is what `mapOpenImConversationToApiChat`
// reads verbatim as `id` in the first place.
export async function markOpenImConversationRead(conversationId: string): Promise<void> {
  await callOpenIm('markConversationMessageAsRead', nextOperationId(), conversationId);
}

// `open_im_sdk.DeleteConversationAndDeleteAllMsg(callback, operationID string, conversationID
// string)` (openim-sdk-core `open_im_sdk/conversation_msg.go:201`) -- verified against the Go
// source. Same as `markOpenImConversationRead` above, this takes the FULL composite conversationID
// (this app's own `ApiChat.id`), not a bare peer/group id.
export async function deleteOpenImConversation(conversationId: string): Promise<void> {
  await callOpenIm('deleteConversationAndDeleteAllMsg', nextOperationId(), conversationId);
}

// Android `TgImOpenImConversationOps.syncPinToOpenIm` → WASM `setConversation` with `{ isPinned }`.
// `conversationId` is the full composite id (`ApiChat.id`), same as mark/delete above.
export async function pinOpenImConversation(conversationId: string, isPinned: boolean): Promise<void> {
  await callOpenIm(
    'setConversation',
    nextOperationId(),
    conversationId,
    JSON.stringify({ isPinned }),
  );
}
